import {
  ArrayField,
  Button,
  Datagrid,
  FunctionField,
  ReferenceManyField,
  SimpleShowLayout,
} from 'react-admin';
import { TableCell, TableHead, TableRow, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { useWellnessUtilization } from '@pumpkincare/users';
import {
  getEmployerTier,
  getTier,
  useWellnessProducts,
} from '@pumpkincare/wellness';

import DateFieldIgnoringZone from '../../../../components/DateFieldIgnoringZone';
import RoutePaths from '../../../../routes';
import { formatCurrency } from '../../../../shared/utils';
import { AuditLogsLink } from '../../../audit-logs';
import { ORIGIN_ORDER_ENUM } from '../../../quotes/quoteFlow/constants/originOrderEnum';
import {
  getWellnessPlanToShowUtilization,
  getWellnessPrice,
} from '../../utils/pet-utils';
import FieldStatusChip from '../field-status-chip';
import petShowStyle from '../pet-show-style';

function CustomDatagridHeader({ children, rowTextAlign = 'left' }) {
  const classes = petShowStyle();

  return (
    <TableHead>
      <TableRow>
        {children.map((child, idx, arr) => {
          return (
            <TableCell
              key={(child.props.label || child.props.source) + '_' + idx}
              className={classes.colHeader}
              sx={{
                ...(arr.length - 1 === idx ? { borderTopRightRadius: '4px' } : {}),
                ...(idx === 2 || idx === 3 ? { textAlign: rowTextAlign } : {}),
              }}
            >
              {child.props.label || child.props.source}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

CustomDatagridHeader.propTypes = {
  children: PropTypes.node,
  rowTextAlign: PropTypes.string,
};

function WellnessDatagrid({ record, onPetCancelClick, ...props }) {
  const classes = petShowStyle();
  const isVetPlan = record?.wellness?.is_vet_plan;
  const { data: wellnessUtilizationData, isLoading: isWellnessUtilizationLoading } =
    useWellnessUtilization(
      getWellnessPlanToShowUtilization(record?.wellness_plans)?.id
    );

  const { data: productData } = useWellnessProducts();

  return (
    <>
      <Typography className={classes.title} sx={{ margin: '24px 0 24px 0' }}>
        Wellness
      </Typography>

      <Typography
        variant='body1'
        style={{ fontWeight: '700', marginBottom: '16px' }}
      >
        Plan Details
      </Typography>

      <SimpleShowLayout className={classes.root} {...props}>
        <ReferenceManyField
          label={''}
          reference={RoutePaths.wellness}
          target='pet_id'
          sort={{ field: 'created_at', order: 'DESC' }}
        >
          <Datagrid
            bulkActionButtons={false}
            header={<CustomDatagridHeader rowTextAlign='left' />}
            data-id='wellness-datagrid'
          >
            <FunctionField
              label='Year'
              render={(record = {}) => record.year?.toString().padStart(4, '0')}
              headerClassName={classes.colHeader}
            />
            <FieldStatusChip
              label='status'
              source='status'
              headerClassName={classes.colHeader}
            />

            <FunctionField
              label='Cost'
              render={(record = {}) =>
                formatCurrency(getWellnessPrice(record, record.is_annualized))
              }
              headerClassName={classes.colHeader}
            />
            <FunctionField
              label='Description'
              render={(record = {}) => {
                const tier = record.group_enrolled
                  ? getEmployerTier(record.tier, record.program_id)
                  : getTier(record.tier, record.program_id);

                return `${tier}`;
              }}
              headerClassName={classes.colHeader}
            />
            <FunctionField
              label='Effective Date'
              render={(record = {}) => {
                return <DateFieldIgnoringZone source='start_date' record={record} />;
              }}
              headerClassName={classes.colHeader}
            />

            <FunctionField
              label='End Date'
              render={(record = {}) => {
                return <DateFieldIgnoringZone source='end_date' record={record} />;
              }}
              headerClassName={classes.colHeader}
            />

            <FunctionField
              label='Is annual?'
              render={(record = {}) => (record.is_annualized ? 'true' : 'false')}
              headerClassName={classes.colHeader}
            />

            <FunctionField
              label='Source'
              render={(record = {}) => {
                const originOrderValue = record.origin_order
                  ? record.origin_order.split('.').at(-1)
                  : null;

                const origin = ORIGIN_ORDER_ENUM.find(
                  item => item.value === originOrderValue
                );
                return origin ? origin.label : '';
              }}
              headerClassName={classes.colHeader}
            />

            <FunctionField
              label=''
              render={(record = {}) => {
                return <AuditLogsLink filter={{ wellness_id: record.id }} />;
              }}
              headerClassName={classes.colHeader}
            />
            <FunctionField
              label=''
              render={(record = {}) => {
                if (record.status === 'renew_pending' && !record.group_enrolled) {
                  return (
                    <Button
                      style={{
                        color: '#D4424E',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                      }}
                      variant='outlined'
                      onClick={() => onPetCancelClick(record)}
                      label='DECLINE'
                    />
                  );
                } else {
                  return null;
                }
              }}
              headerClassName={classes.colHeader}
            />
          </Datagrid>
        </ReferenceManyField>
        <Typography
          variant='body1'
          style={{ fontWeight: '700', marginBottom: '16px', marginTop: '32px' }}
        >
          Utilization
        </Typography>
        <ArrayField
          label=''
          source='wellness_utilization'
          record={{
            wellness_utilization: isWellnessUtilizationLoading
              ? []
              : wellnessUtilizationData.entitlements.filter(
                  entitlement => entitlement.entitlement !== 'TELEHEALTH'
                ),
          }}
        >
          <Datagrid
            bulkActionButtons={false}
            header={<CustomDatagridHeader rowTextAlign='right' />}
            sx={{
              '& .RaDatagrid-rowCell': { textAlign: 'right' },
              '& .RaDatagrid-tableWrapper': { width: '650px' },
            }}
          >
            <FunctionField
              label='Item name'
              render={(record = {}) => (
                <div style={{ textAlign: 'left' }}>
                  {
                    productData?.wellness_products.find(
                      item => item.entitlement === record.entitlement
                    )?.name
                  }
                </div>
              )}
              headerClassName={classes.colHeader}
            />
            <FunctionField
              label='Item description'
              render={(record = {}) => record.description}
              headerClassName={classes.colHeader}
            />
            <FunctionField
              label={isVetPlan ? 'Total Qty' : 'Total Amount'}
              render={(record = {}) =>
                isVetPlan
                  ? record.quantity_available
                  : formatCurrency(record.available_amount, {
                      hideZero: true,
                      areCents: true,
                    })
              }
              headerClassName={classes.colHeader}
            />
            <FunctionField
              label={isVetPlan ? 'Used Qty' : 'Used Amount'}
              render={(record = {}) =>
                isVetPlan
                  ? record.quantity_utilized
                  : formatCurrency(record.utilized_amount, {
                      hideZero: true,
                      areCents: true,
                    })
              }
              headerClassName={classes.colHeader}
            />
          </Datagrid>
        </ArrayField>
      </SimpleShowLayout>
    </>
  );
}

WellnessDatagrid.propTypes = {
  record: PropTypes.object.isRequired,
  onPetCancelClick: PropTypes.func.isRequired,
};

export default WellnessDatagrid;
